import { Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import { isEqual, setHours, setMinutes, startOfDay } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useCallback, useEffect, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDateFormat, useFormAnalytics, useIsAnyResource, useIsAvailabilitiesFetching, useTimeZones } from '../../../../../../hooks'
import { theme } from '../../../../../../modules/App/Providers/Theme/theme'
import { AppointmentFormData, AvailabilitiesResponse, ResourcePair, TimeZones } from '../../../../../../types'
import { determineResourceIds } from '../../../../../../util/determineResourceIds'
import { ANY_INTERNAL_RESOURCE_ID, ANY_EXTERNAL_RESOURCE_ID } from '../../../../../../constants'
import { isAnyResourceId } from '../../../../../../util'
import { Loader } from '../../../../../Loader'
import { Spacer } from '../../../../../Spacer'
import { TimePicker } from '../../../../../forms'
import { useSpecialist } from '../../../useSpecialist'
import { useTimeEntryMode } from '../../../useTimeEntryMode'
import { useAvailability } from '../../Providers/Availability'
import { useAvailabilityParameters } from '../../useAvailabilityParameters'
import { useFitterRelativeTimeInfo } from '../../../../useFitterRelativeTimeInfo'
import { Availabilities } from './Availabilities'
import { DateTitle } from './DateTitle'
import { HelperTitle } from './HelperTitle'
import { ToggleModeButton } from './ToggleModeButton'
import { useTranslation } from 'react-i18next'
import { filterByResource } from 'util/filterByResource'

type TimeFormData = Pick<
  AppointmentFormData,
  'clientStartDate' | 'clientStartTime' | 'customer_timezone' | 'fitter_timezone' | 'month_date' | 'resource_ids' | 'start_date'
>

interface Props {
  availability: AvailabilitiesResponse | undefined
}

const StyledLoader = styled(Loader)`
  position: absolute;
  right: 0;
  top: 0.375rem;
`

const TitleContainer = styled('div')`
  position: relative;
`

const NotSetup = styled('div')`
  color: ${theme.palette.grey[500]};
  display: grid;
  gap: 0.5rem;
  font-size: 0.75rem;
  text-align: center;
`
const Container = styled(Grid)<{
  isFitterSchedulingMode?: boolean
}>`
  @media (max-width: ${theme.breakpoints.values.md}px) {
    padding-top: 0 !important;
  }
`

const timeZoneToFormattedTimeZoneText = (timeZones: TimeZones, timeZone: string) => {
  const { alternative_name, cities } = timeZones[timeZone]
  const [city] = cities

  return `${alternative_name} / ${city}`
}

const Content = ({ availability }: Props) => {
  const { beManualMode, isManualMode } = useTimeEntryMode()
  const { onInputEntered, onInputValid } = useFormAnalytics<TimeFormData>()
  const { timeZones } = useTimeZones()
  const { t } = useTranslation()
  const { specialist } = useSpecialist()
  const isAvailabilitiesFetching = useIsAvailabilitiesFetching()
  const params = useAvailabilityParameters()
  const resources = params?.resources || []
  const isAnyResourceSelected = useIsAnyResource()
  const { setValue } = useFormContext<AppointmentFormData>()
  const { data: availabilityData } = useAvailability()
  const { dateToISODateKey } = useDateFormat()

  const dayAvailability = availabilityData?.dayAvailability

  const [clientStartDate, customer_timezone, fitter_timezone, month_date, specialist_id, start_date] = useWatch({
    name: ['clientStartDate', 'customer_timezone', 'fitter_timezone', 'month_date', 'specialist_id', 'start_date'],
  })

  const fitterRelativeTimeInfo = useFitterRelativeTimeInfo(start_date, customer_timezone, fitter_timezone)
  const resourceHasIntegration = Boolean(!availability || availability?.resourceHasCalendarIntegration?.[specialist_id as string])

  useEffect(() => beManualMode(!resourceHasIntegration), [resourceHasIntegration])

  // Clear eligibleResourcePairs when the selected date changes
  useEffect(() => {
    setValue('eligibleResourcePairs', undefined)
  }, [clientStartDate, setValue])

  // Use a ref for dayAvailability: it should be read by the effect but should NOT trigger it.
  // dayAvailability can get new references when availability data is reprocessed even though
  // its content is the same — triggering on it would cause an infinite setValue → re-render loop.
  const dayAvailabilityRef = useRef(dayAvailability)
  dayAvailabilityRef.current = dayAvailability

  useEffect(() => {
    const currentDayAvailability = dayAvailabilityRef.current
    if (!(isAnyResourceSelected && start_date && currentDayAvailability)) {
      return
    }

    const dayKey = dateToISODateKey(start_date)
    const dayResourceTimeBlocks = currentDayAvailability[dayKey]

    if (!dayResourceTimeBlocks) return

    // When filtering by internal/external, build a set of matching resource IDs
    const resourceTypeFilter =
      specialist_id === ANY_INTERNAL_RESOURCE_ID || specialist_id === ANY_EXTERNAL_RESOURCE_ID ? filterByResource(specialist_id) : undefined
    const allowedResourceIds = resourceTypeFilter ? new Set(resources?.filter(resourceTypeFilter).map((r) => r.id)) : undefined

    // Collect all eligible resource pairs [fitterId, cabinId?] for the selected time slot
    const eligibleResourcePairs = Object.entries(dayResourceTimeBlocks).reduce((acc, [resourceId, resourceTimeBlocks]) => {
      if (isAnyResourceId(resourceId)) {
        return acc
      }

      if (allowedResourceIds && !allowedResourceIds.has(resourceId)) {
        return acc
      }

      // Find the time block that matches the selected start_date
      const matchingBlock = resourceTimeBlocks.find(({ date_start: blockStart }) => isEqual(blockStart, start_date))

      if (!matchingBlock) {
        return acc
      }

      // Build the ResourcePair tuple: [fitterId, cabinId?]
      const cabinId = matchingBlock.extraResourceIds?.[0]
      const resourcePair: ResourcePair = cabinId ? [resourceId, cabinId] : [resourceId]
      acc.push(resourcePair)

      return acc
    }, [] as ResourcePair[])

    setValue('eligibleResourcePairs', eligibleResourcePairs)
  }, [isAnyResourceSelected, start_date, specialist_id])

  const onTimeChange = useCallback(
    (date: Date | null) => {
      if (date) {
        onInputValid('clientStartTime')

        // Convert the selected time to UTC for start_date
        const startTimeWithHourMinutes = setMinutes(setHours(startOfDay(clientStartDate), date.getHours()), date.getMinutes())
        const utcStartDate = zonedTimeToUtc(startTimeWithHourMinutes, customer_timezone as string)

        setValue('start_date', utcStartDate)

        // Determine resource_ids based on specialist availability and cabin availability
        const resourceIds = determineResourceIds({
          specialistId: specialist_id as string | undefined,
          dayAvailability,
          clientStartDate,
          dateToISODateKey,
        })
        if (resourceIds) {
          setValue('resource_ids', resourceIds)
        }
      }
    },
    [onInputValid, clientStartDate, customer_timezone, specialist_id, dayAvailability, dateToISODateKey, setValue]
  )

  const { fitterTime, isDayAfter, isDayBefore } = fitterRelativeTimeInfo()
  const hasSelectedADay = Boolean(clientStartDate)
  const resource = resources?.find(({ id }) => id === specialist_id)
  const fitter = resource?.name ? `/ ${resource?.name}` : ''

  let dayDiff = ''

  switch (true) {
    case isDayAfter:
      dayDiff = ' (+1 day)'
      break
    case isDayBefore:
      dayDiff = ' (-1 day)'
      break
    default:
      break
  }

  const areInSameTimeZone = customer_timezone === fitter_timezone
  const helperText = !start_date || areInSameTimeZone ? '' : `${fitterTime}${dayDiff} for the ${t(specialist?.title ?? '')} ${fitter}`
  const formattedTimeZone = timeZones && !areInSameTimeZone ? `(${timeZoneToFormattedTimeZoneText(timeZones, customer_timezone)})` : ''
  const label = t('common.label.timezoneWithCustomerTimePrefix', {
    customerTimeText: t('common.label.customerTime'),
    timezone: formattedTimeZone,
  })

  const hasSelectedResourceAndDay = Boolean(specialist_id && hasSelectedADay)
  const misconfiguredIntegrationText = availability?.hasAppointmentTypeMappingError
    ? `The selected appointment type is not set up for this ${specialist?.name}.`
    : `The selected ${specialist?.name} is not set up in our system.`

  return (
    <Grid container spacing={3} sx={{ p: 0 }}>
      <Container data-availabilities item xs={12}>
        <TitleContainer>
          {hasSelectedADay ? <DateTitle /> : <HelperTitle />}
          {isAvailabilitiesFetching && <StyledLoader />}
        </TitleContainer>
        {hasSelectedResourceAndDay && resourceHasIntegration && !isAnyResourceSelected && <ToggleModeButton />}
        <Spacer />
        {hasSelectedResourceAndDay && (
          <>
            {isManualMode ? (
              <>
                <Spacer count={0.5} />
                <TimePicker
                  helperText={helperText}
                  label={label}
                  name="clientStartTime"
                  onChange={onTimeChange}
                  onFocus={() => onInputEntered('clientStartTime')}
                />
                {!resourceHasIntegration && (
                  <>
                    <Spacer />
                    <NotSetup>
                      <div>{misconfiguredIntegrationText}</div>
                      <div>You can still add a time manually but this will not be reflected on the provider&apos;s calendar.</div>
                    </NotSetup>
                  </>
                )}
              </>
            ) : (
              <>{month_date ? <Availabilities /> : null}</>
            )}
          </>
        )}
      </Container>
    </Grid>
  )
}

export { Content }
